<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useOnboardingTrigger } from '../model'
import { useModalStore, modalStateEnum } from '@shared/model'
import { useUserProfileStore } from '@entities/user'

const props = defineProps<{
  /** Включена ли программа лояльности (если нет — блок баллов/лесенки скрыт). */
  loyaltyEnabled?: boolean
  /** Активный режим приветствия: приветственные баллы или скидка на первые заказы. */
  welcomeMode?: 'bonus_points' | 'first_orders_discount'
  /** Приветственные баллы, начисленные при регистрации (режим bonus_points). */
  welcomePoints?: number
  /** Длина «лесенки»: на сколько первых заказов действует скидка (режим first_orders_discount). */
  firstOrdersCount?: number
  /** Есть ли у пользователя сохранённый адрес (если да — блок «сохраните адрес» скрыт). */
  hasAddress?: boolean
}>()

const emit = defineEmits<{ close: [] }>()

const modalStore = useModalStore()
const userProfile = useUserProfileStore()
const { markOnboardingSeen } = useOnboardingTrigger()

const userName = computed(() => userProfile.getUser?.first_name?.trim() || '')
// Источник истины — modalData (пропсы DynamicModal фиксируются один раз при монтировании
// и устаревают, если модалку открыли поверх уже смонтированного DynamicModal). Пропсы — фолбэк.
// Программа лояльности выключена по умолчанию считается включённой только если явно не false.
const loyaltyEnabled = computed(() => (modalStore.modalData?.loyaltyEnabled ?? props.loyaltyEnabled) !== false)
const welcomeMode = computed(() => modalStore.modalData?.welcomeMode ?? props.welcomeMode ?? 'bonus_points')
const welcomePoints = computed(() => Number(modalStore.modalData?.welcomePoints ?? props.welcomePoints) || 0)
const firstOrdersCount = computed(() => Number(modalStore.modalData?.firstOrdersCount ?? props.firstOrdersCount) || 0)
const hasAddress = computed(() => Boolean(modalStore.modalData?.hasAddress ?? props.hasAddress))

// Если лояльность выключена — ни баллов, ни лесенки. Иначе режим взаимоисключающий:
// показываем баллы ЛИБО лесенку, смотря что активно и заполнено.
const showPointsBlock = computed(() => loyaltyEnabled.value && welcomeMode.value === 'bonus_points' && welcomePoints.value > 0)
const showLadderBlock = computed(() => loyaltyEnabled.value && welcomeMode.value === 'first_orders_discount' && firstOrdersCount.value > 0)

/** Склонение слова «заказ» по числу: 1 заказ, 2 заказа, 5 заказов. */
function pluralizeOrders(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'заказ'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'заказа'
  return 'заказов'
}
const ordersWord = computed(() => pluralizeOrders(firstOrdersCount.value))

// Помечаем онбординг просмотренным сразу при показе — повторно не всплывёт,
// даже если пользователь закроет модалку крестиком.
onMounted(() => {
  markOnboardingSeen(userProfile.getUser?.id)
})

/** Перейти к выбору адреса доставки на карте (создание нового адреса). */
function addAddress(): void {
  modalStore.open(modalStateEnum.ModalMapYandex, { mode: 'create', orderType: 'delivery' })
}

/** Пропустить — закрыть модалку. */
function skip(): void {
  emit('close')
}
</script>

<template>
  <div class="flex flex-col gap-5 px-3 pb-3 pt-1 text-center sm:px-4 max-lg:min-h-full max-lg:justify-center">
    <!-- Благодарность -->
    <div class="flex flex-col items-center gap-2">
      <div class="flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-500/15">
        <UIcon name="i-heroicons-gift" class="h-8 w-8 text-orange-500" />
      </div>
      <h3 class="text-xl font-bold text-gray-900 dark:text-white">
        Ура, регистрация завершена! Привет<template v-if="userName">, {{ userName }}</template> 👋
      </h3>
    </div>

    <!-- Приветственные баллы (режим bonus_points) -->
    <div
      v-if="showPointsBlock"
      class="flex items-start gap-3 rounded-2xl bg-orange-50 p-4 text-left dark:bg-orange-500/10"
    >
      <UIcon name="i-heroicons-sparkles" class="mt-0.5 h-6 w-6 shrink-0 text-orange-500" />
      <p class="text-sm text-gray-700 dark:text-gray-200">
        Лови <span class="font-bold text-orange-600 dark:text-orange-400">{{ welcomePoints }}</span>
        приветственных баллов — это твоя скидка на первый заказ.
      </p>
    </div>

    <!-- Скидка на первые заказы (режим first_orders_discount) -->
    <div
      v-else-if="showLadderBlock"
      class="flex items-start gap-3 rounded-2xl bg-orange-50 p-4 text-left dark:bg-orange-500/10"
    >
      <UIcon name="i-heroicons-sparkles" class="mt-0.5 h-6 w-6 shrink-0 text-orange-500" />
      <p class="text-sm text-gray-700 dark:text-gray-200">
        Лови скидку на первые
        <span class="font-bold text-orange-600 dark:text-orange-400">{{ firstOrdersCount }}</span>
        {{ ordersWord }} — это наш подарок за регистрацию.
      </p>
    </div>

    <!-- Предложение сохранить адрес доставки -->
    <template v-if="!hasAddress">
      <div class="flex items-start gap-3 rounded-2xl border border-gray-100 p-4 text-left dark:border-gray-800">
        <UIcon name="i-heroicons-map-pin" class="mt-0.5 h-6 w-6 shrink-0 text-orange-500" />
        <div>
          <h4 class="text-base font-semibold text-gray-900 dark:text-white">
            Сохраним адрес доставки?
          </h4>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Один раз укажешь — и больше не вводишь. В следующий раз доставку оформишь
            в один клик. Удобно ведь 🙂
          </p>
        </div>
      </div>

      <div class="flex flex-row gap-3">
        <UiButtonMain
          class="flex-1 !bg-gray-900 !text-white hover:!bg-gray-800 dark:!bg-gray-800 dark:hover:!bg-gray-700"
          size="xl"
          color="red"
          block
          label="Не сейчас"
          @click="skip"
        />
        <UiButtonMain class="flex-1" size="xl" color="red" block label="Добавить адрес" @click="addAddress" />
      </div>
    </template>

    <UiButtonMain v-else size="xl" color="red" block label="Отлично!" @click="skip" />
  </div>
</template>

<style scoped lang="scss"></style>
